// frontend/src/components/Cards/RequiredDispatchProgressCard.js

import React from 'react';
import { Paper, Box, Typography, Tooltip, Chip, LinearProgress, IconButton } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning'; // Imported for blinking icon
import { useTheme, styled, alpha } from '@mui/material/styles';
import { getColorByPercentage } from '../../utils/colorUtils';

// Styled component for blinking attention icon
const BlinkingWarningIcon = styled(WarningIcon)(({ theme }) => ({
    color: theme.palette.error.main,
    animation: 'blinker 1s linear infinite',
    '@keyframes blinker': {
        '50%': { opacity: 0 },
    },
    marginLeft: theme.spacing(0.5),
}));

const RequiredDispatchProgressCard = ({
    icon: Icon,
    label,
    requiredDispatch,
    reshipOrders, // New prop
    progressPercentage,
    tooltip,
    badge,
    compact = false,
    sx,
}) => {
    const theme = useTheme();
    const dispatchPalette = theme.palette.dispatch;
    const dynamicColor = getColorByPercentage(progressPercentage, dispatchPalette);

    return (
        <Paper
            elevation={compact ? 0 : 3}
            sx={{
                p: compact ? 1.25 : 3,
                borderRadius: 2,
                backgroundColor: compact ? '#fcfcfd' : '#fff',
                border: compact ? '1px solid' : 'none',
                borderColor: compact ? 'divider' : 'transparent',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                overflow: 'hidden',
                ...sx,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: compact ? 30 : { xs: 40, sm: 56 },
                            width: compact ? 30 : { xs: 40, sm: 56 },
                            backgroundColor: compact ? alpha(dynamicColor, 0.12) : dynamicColor,
                            color: compact ? dynamicColor : '#fff',
                            borderRadius: compact ? 1.25 : '50%',
                            mr: compact ? 1.25 : 2,
                            transition: 'background-color 0.3s ease',
                        }}
                    >
                        <Icon fontSize={compact ? 'small' : 'large'} />
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                            <Typography
                                variant={compact ? 'body2' : 'h5'}
                                sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}
                            >
                                {label}
                            </Typography>
                            {badge && (
                                <Chip
                                    label={badge}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        ml: 0.75,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                    }}
                                    aria-label={`${badge} badge`}
                                />
                            )}
                            {tooltip && (
                                <Tooltip title={tooltip} arrow>
                                    <IconButton size="small" sx={{ ml: 0.25, p: compact ? 0.25 : 0.5 }}>
                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Typography
                            variant={compact ? 'subtitle1' : 'h4'}
                            sx={{
                                fontWeight: 800,
                                color: dynamicColor,
                                transition: 'color 0.3s ease',
                                mt: compact ? 0.25 : 0,
                            }}
                        >
                            {requiredDispatch}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Re-ship queue */}
            {reshipOrders > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: compact ? 0.5 : 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.error.main,
                        }}
                        aria-label={`Re-ship queue: ${reshipOrders}`}
                    >
                        Re-ship queue: {reshipOrders}
                    </Typography>
                    <BlinkingWarningIcon aria-label={`Re-ship queue: ${reshipOrders}`} />
                </Box>
            )}

            {/* Progress Bar */}
            <Box sx={{ mt: compact ? 0.5 : 2 }}>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                    Queue progress
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={progressPercentage > 100 ? 100 : progressPercentage}
                    sx={{
                        height: compact ? 8 : 10,
                        borderRadius: 5,
                        [`& .MuiLinearProgress-bar`]: {
                            backgroundColor: dynamicColor,
                            transition: 'background-color 0.3s ease',
                        },
                        transition: 'background-color 0.3s ease',
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        mt: 0.75,
                        fontWeight: 700,
                        color: dynamicColor,
                        transition: 'color 0.3s ease',
                        fontSize: { xs: '0.78rem', sm: '0.8rem' },
                    }}
                >
                    {progressPercentage > 100 ? 100 : progressPercentage}%
                </Typography>
            </Box>
        </Paper>
    );

};

export default RequiredDispatchProgressCard;
